
import { APP_VERSION } from "../version.js";
import { locale } from "../services/LocaleService.js";

/**
 * PWAManager
 * 
 * Handles Service Worker registration, updates, and PWA-related UI notifications.
 * Decoupled from main business logic for stability.
 */
export class PWAManager {
    static LATEST_VERSION = APP_VERSION; // [FIX] Synced with global version 

    constructor() {
        this.registration = null;
        this.isResetting = false;
    }

    /**
     * Initialize PWA Manager
     */
    async init() {
        if (!('serviceWorker' in navigator)) {
            console.warn("Service Worker not supported in this browser.");
            return;
        }

        // v4.8 Force Reset Protocol (One-time cleanup for stability)
        await this._handleForceResetIfNeeded();

        // REMOVED 'load' listener to prevent race conditions. 
        // Script is defer, so it runs after parsing.
        this._registerServiceWorker();


        // Handle controller change — NO AUTO-RELOAD
        // [FIX v1.36.489] Removed window.location.reload() to prevent forced refresh during content viewing
        // Users will get the new version when they click the update button or revisit the site
        navigator.serviceWorker.addEventListener('controllerchange', () => {
            console.log("[PWA] Controller changed. New SW active. Will apply on next visit or update click.");
        });

        // Listen for PWA Install Prompt (Android/Chrome)
        window.addEventListener('beforeinstallprompt', (e) => {
            e.preventDefault();
            this.deferredPrompt = e;
            this._showInstallPromotion();
        });

        // Detect iOS and show custom guide
        this._checkIosAndShowGuide();

        window.addEventListener('appinstalled', () => {
            this._hideInstallPromotion();
            this.deferredPrompt = null;
            console.log('PWA was installed');
        });

        // Auto-Update Policy: Check every 15 minutes and when app becomes visible
        setInterval(() => this.checkForUpdates(), 15 * 60 * 1000);

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                this.checkForUpdates();
            }
        });
    }

    /**
     * Public method to manually trigger update check
     */
    async checkForUpdates() {
        if (!this.registration) return;
        try {
            console.log('🔍 Checking for PWA updates...');
            await this.registration.update();
        } catch (e) {
            console.error('Update check failed:', e);
        }
    }

    _checkIosAndShowGuide() {
        const isIos = /iPhone|iPad|iPod/.test(navigator.userAgent) && !window.MSStream;
        const isStandalone = window.navigator.standalone === true || window.matchMedia('(display-mode: standalone)').matches;

        // DEBUG: Force logs
        console.log(`[iOS Check] isIos: ${isIos}, isStandalone: ${isStandalone}`);

        if (isIos && !isStandalone) {
            // Show iOS specific guide quickly (1s)
            setTimeout(() => this._showIosInstallGuide(), 1000);
        }
    }

    _showIosInstallGuide() {
        // REMOVED SESSION CHECK FOR DEBUGGING/VISIBILITY
        // if (sessionStorage.getItem('suppress_ios_guide')) return;

        const toast = document.createElement('div');
        toast.className = 'fixed bottom-4 left-4 right-4 z-[9999] bg-white/90 backdrop-blur-md border-t border-gray-200 p-6 rounded-t-2xl shadow-[0_-10px_40px_rgba(0,0,0,0.2)] animate-slide-up flex flex-col gap-4';
        toast.innerHTML = `
            <div class="flex justify-between items-start">
                <div>
                    <h3 class="font-bold text-lg text-gray-900">${locale.t('pwa.ios_guide_title')}</h3>
                    <p class="text-sm text-gray-500 mt-1">${locale.t('pwa.ios_guide_desc')}</p>
                </div>
                <button id="close-ios-guide" class="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors">
                    <span class="material-symbols-outlined text-gray-600">close</span>
                </button>
            </div>
            
            <div class="flex flex-col gap-4 mt-2">
                <div class="flex items-center gap-4">
                    <div class="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg">
                        <span class="material-symbols-outlined text-blue-500">ios_share</span>
                    </div>
                    <span class="text-sm font-medium text-gray-700">1. ${locale.t('pwa.ios_step1')}</span>
                </div>
                <div class="flex items-center gap-4">
                    <div class="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg">
                        <span class="material-symbols-outlined text-gray-900">add_box</span>
                    </div>
                    <span class="text-sm font-medium text-gray-700">2. ${locale.t('pwa.ios_step2')}</span>
                </div>
            </div>
            <div class="w-full h-1 bg-gray-100 rounded-full mt-2"></div>
        `;
        document.body.appendChild(toast);

        document.getElementById('close-ios-guide').onclick = () => {
            toast.remove();
            sessionStorage.setItem('suppress_ios_guide', 'true');
        };
    }

    _showInstallPromotion() {
        // Prevent duplicate install toasts
        if (document.getElementById('install-toast')) return;
        // Check session suppression
        if (sessionStorage.getItem('suppress_install_toast')) return;

        const toast = document.createElement('div');
        toast.id = 'install-toast';
        toast.className = 'fixed bottom-4 left-4 right-4 z-[9999] bg-white text-gray-900 px-6 py-4 rounded-xl shadow-2xl flex items-center justify-between border border-gray-200 animate-slide-up';
        toast.innerHTML = `
            <div class="flex items-center gap-4">
                <div class="bg-indigo-100 p-2 rounded-lg">
                    <span class="material-symbols-outlined text-indigo-600">download</span>
                </div>
                <div class="flex flex-col">
                    <span class="font-bold text-sm">${locale.t('pwa.install_title')}</span>
                    <span class="text-xs text-gray-500">${locale.t('pwa.install_desc')}</span>
                </div>
            </div>
            <div class="flex gap-2">
                <button id="pwa-install-btn" class="bg-indigo-600 text-white px-4 py-2 rounded-lg font-bold text-xs hover:bg-indigo-700 transition-colors shadow-sm">
                    ${locale.t('pwa.install_btn')}
                </button>
                <button id="close-install-btn" class="text-gray-400 hover:text-gray-600 p-1 rounded">
                    <span class="material-symbols-outlined text-sm">close</span>
                </button>
            </div>
        `;
        document.body.appendChild(toast);

        // Close Button Handler
        document.getElementById('close-install-btn').onclick = () => {
            this._hideInstallPromotion();
            sessionStorage.setItem('suppress_install_toast', 'true');
        };

        // Install Button Handler
        const installBtn = document.getElementById('pwa-install-btn');
        if (installBtn) {
            installBtn.addEventListener('click', async () => {
                this._hideInstallPromotion();
                // Show the install prompt
                if (this.deferredPrompt) {
                    this.deferredPrompt.prompt();
                    // Wait for the user to respond to the prompt
                    const { outcome } = await this.deferredPrompt.userChoice;
                    console.log(`User response to the install prompt: ${outcome}`);
                    // We've used the prompt, and can't use it again, throw it away
                    this.deferredPrompt = null;
                }
            });
        }
    }

    _hideInstallPromotion() {
        const toast = document.getElementById('install-toast');
        if (toast) toast.remove();
    }


    /**
     * Check if we need to perform a nuclear reset of caches/registrations
     * This is useful when deploying major structural changes to PWA
     */
    async _handleForceResetIfNeeded() {
        // Change this key whenever a 'Nuclear Reset' is required
        const RESET_KEY = 'mirinae_v1.36.495_pwa_stabilize'; // [FIX] PWA Loop Fix Reset

        if (localStorage.getItem(RESET_KEY)) {
            return; // Already reset, proceed normally
        }

        console.log("🚀 v4.8 PWA Force Reset Initiated...");
        this.isResetting = true;

        try {
            // 1. Unregister all existing Service Workers
            const registrations = await navigator.serviceWorker.getRegistrations();
            for (let registration of registrations) {
                await registration.unregister();
                console.log("Unregistered old SW:", registration);
            }

            // 2. Clear All Caches
            const keys = await caches.keys();
            await Promise.all(keys.map(key => caches.delete(key)));
            console.log("Cleared all caches.");

            // 3. Mark Done
            localStorage.setItem(RESET_KEY, 'true');

            // 4. Force Reload to start fresh
            console.log("Reset complete. Reloading...");
            window.location.reload(true);
        } catch (error) {
            console.error("Force reset failed:", error);
            // Even if it fails, mark as done to prevent infinite loops
            localStorage.setItem(RESET_KEY, 'true');
        }
    }

    /**
     * Register the Service Worker
     */
    _registerServiceWorker() {
        if (this.isResetting) return;

        // [FIX v4.81] Removed '?v=APP_VERSION' to prevent PWA Update Infinite Loops!
        // Browsers automatically detect byte-level changes in sw.js.
        // Forcing a query parameter causes a ping-pong conflict between the cached index.html
        // pointing to old URL and network pointing to new URL.
        const swUrl = `/sw.js`;

        // [FIX] Emergency Loop Breaker
        // If we detect excessive reloads (e.g., 3 in 20s), unregister SW entirely.
        const bootCount = parseInt(localStorage.getItem('pwa_boot_count') || '0');
        const bootTime = parseInt(localStorage.getItem('pwa_boot_time') || '0');
        const now = Date.now();

        if (now - bootTime < 30000) {
            // High frequency restart detected
            if (bootCount > 5) {
                console.error("🚨 PWA Infinite Loop Detected! Unregistering Service Worker...");
                this._emergencyUnregister();
                alert("PWA 오류가 감지되어 초기화했습니다. 잠시 후 다시 시도해주세요.");
                localStorage.setItem('pwa_boot_count', '0');
                return;
            }
            localStorage.setItem('pwa_boot_count', (bootCount + 1).toString());
        } else {
            // Reset counter if stable for 30s
            localStorage.setItem('pwa_boot_time', now.toString());
            localStorage.setItem('pwa_boot_count', '1');
        }

        navigator.serviceWorker.register(swUrl)
            .then(registration => {
                this.registration = registration;
                console.log('✅ Service Worker registered:', registration);

                // Check for updates
                console.log('[PWA] Checking for updates on registration...');
                this._checkForUpdate(registration);

                // Listen for future updates
                registration.onupdatefound = () => {
                    this._handleUpdateFound(registration);
                };
            })
            .catch(error => {
                console.error('❌ SW registration failed:', error);
                // Critical Error Feedback
                alert("PWA Error: Service Worker Failed to Register.\n" + error.message);
            });
    }

    _showDebugToast(msg) {
        const toast = document.createElement('div');
        toast.className = 'fixed bottom-4 left-4 z-[9999] bg-black/80 text-white px-3 py-1 text-xs rounded pointer-events-none';
        toast.textContent = msg;
        document.body.appendChild(toast);
        setTimeout(() => toast.remove(), 3000);
    }

    /**
     * Check if there is already a waiting worker (update downloaded but not activated)
     */
    _checkForUpdate(registration) {
        if (registration.waiting) {
            this._showUpdateNotification(registration);
        }
    }

    /**
     * Handle 'onupdatefound' event
     */
    _handleUpdateFound(registration) {
        const installingWorker = registration.installing;
        if (installingWorker == null) return;

        installingWorker.onstatechange = () => {
            if (installingWorker.state === 'installed') {
                if (navigator.serviceWorker.controller) {
                    // New update available
                    console.log('📦 New content is available; please refresh.');
                    this._showUpdateNotification(registration);
                } else {
                    console.log('✅ Content is cached for offline use.');
                }
            }
        };
    }

    /**
     * Show the Update Toast Notification
     * @param {ServiceWorkerRegistration} registration 
     */
    _showUpdateNotification(registration) {
        // NOTE: In-App Browser detection logic REMOVED.
        // We now show the update button EVERYWHERE to ensure updates work.

        // [FIX v1.36.489] Check session suppression
        if (sessionStorage.getItem('suppress_update_toast')) return;

        // Prevent duplicate toasts
        if (document.getElementById('update-toast')) return;

        // Create Toast UI
        const toast = document.createElement('div');
        toast.id = 'update-toast';
        // [FIX v4.81] Removed session suppression to ensure critical updates are seen
        toast.className = 'fixed top-4 right-4 z-[9999] bg-indigo-600 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-4 animate-bounce';
        toast.innerHTML = `
            <div class="flex flex-col">
                <span class="font-bold text-sm">${locale.t('pwa.update_title_v2')}</span>
                <span class="text-xs opacity-80">${locale.t('pwa.update_desc')}</span>
            </div>
            <div class="flex gap-2">
                <button id="update-btn" class="bg-white text-indigo-600 px-4 py-2 rounded-lg font-bold text-xs hover:bg-gray-100 transition-colors shadow-sm">
                    ${locale.t('pwa.update_btn')}
                </button>
                <button id="close-toast-btn" class="text-white hover:bg-indigo-700 p-1 rounded">
                    <span class="material-symbols-outlined text-sm">close</span>
                </button>
            </div>
        `;
        document.body.appendChild(toast);

        // Close Button Handler
        document.getElementById('close-toast-btn').onclick = () => {
            // [FIX v1.36.489] Suppress for this session to prevent infinite toast loop
            sessionStorage.setItem('suppress_update_toast', 'true');
            toast.remove();
        };

        // Update Button Handler
        const updateBtn = document.getElementById('update-btn');
        if (updateBtn) {
            // Apply both click and touch listeners for robust mobile support
            ['click', 'touchend'].forEach(evt =>
                updateBtn.addEventListener(evt, (e) => this._handleUpdateClick(e, registration))
            );
        }
    }

    /**
     * Handle the user clicking "Update"
     */
    async _handleUpdateClick(e, registration) {
        e.preventDefault();
        e.stopPropagation();

        const toast = document.getElementById('update-toast');
        if (toast) {
            toast.classList.remove('animate-bounce');
            toast.innerHTML = `<span class="text-sm font-bold flex items-center gap-2"><span class="loader-spin w-4 h-4 border-2 border-white/30 border-t-white inline-block"></span> ${locale.t('pwa.updating')}</span>`;
        }

        console.log("🔄 User triggered update.");

        // [FIX v1.36.496] Suppress update toast for this session BEFORE reload
        // This prevents: reload → detect update again → show toast → loop
        sessionStorage.setItem('suppress_update_toast', 'true');

        if (registration && registration.waiting) {
            // Send message to SW to skip waiting
            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }

        // [FIX v1.36.489] Direct reload after short delay instead of relying on controllerchange
        // This prevents the "updating..." freeze and guarantees the page refreshes
        setTimeout(() => {
            window.location.reload();
        }, 1500);
    }

    /**
     * DEBUG: Manual Trigger for Update Check
     */
    async debugCheckForUpdates() {
        console.log("🔍 [DEBUG] Checking for updates...");
        if (!this.registration) {
            alert("서비스 워커가 등록되지 않았습니다.");
            return;
        }
        try {
            await this.registration.update();
            console.log("🔍 [DEBUG] Update check triggered.");
            alert("업데이트 확인 중... (콘솔 확인 필요)");
        } catch (e) {
            alert("업데이트 확인 실패: " + e.message);
        }
    }

    /**
     * DEBUG: Manual Install Trigger
     */
    debugTriggerInstall() {
        if (!this.deferredPrompt) {
            alert("설치 가능한 상태가 아닙니다. (이미 설치되었거나 브라우저 미지원)");
            return;
        }
        this._showInstallPromotion();
    }
    /**
     * Emergency Unregister
     * Called when loop is detected.
     */
    async _emergencyUnregister() {
        try {
            const registrations = await navigator.serviceWorker.getRegistrations();
            for (let registration of registrations) {
                await registration.unregister();
            }
            console.log("🚨 Emergency Unregister Complete.");
        } catch (e) {
            console.error("Emergency Unregister failed:", e);
        }
    }
}

// Create a singleton instance
export const pwaManager = new PWAManager();

// Expose to window for debugging
window.pwaParams = pwaManager;

